import { api } from './client';

// ── Types ──

export interface CrawlStrategyConfig {
  id: number;
  domain: string;
  strategy: 'quickscraper' | 'quickscraper_premium' | 'browseract' | 'direct';
  render_js: boolean;
  premium_proxy: boolean;
  country_code: string;
  rate_limit_per_min: number;
  rate_limit_delay_ms: number;
  priority: number;
  is_active: boolean;
  notes: string | null;
  success_rate: number;
  total_attempts: number;
  total_success: number;
  last_tested_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CrawlAttemptLog {
  id: number;
  task_id: string;
  url: string;
  domain: string;
  strategy: string;
  render_js: boolean;
  premium_proxy: boolean;
  status: 'success' | 'partial' | 'failed' | 'timeout' | 'blocked';
  http_status: number | null;
  html_size: number;
  has_modules: boolean;
  response_time_ms: number | null;
  error_message: string | null;
  created_at: string;
}

export interface DomainStats {
  domain: string;
  total_strategies: number;
  avg_success_rate: number;
  total_attempts: number;
  today_attempts: number;
  today_failures: number;
}

export interface TestResult {
  url: string;
  strategy: string;
  status: string;
  http_status: number;
  html_size: number;
  response_time_ms: number;
  error_message: string | null;
}

export interface AutoDetectResult {
  domain: string;
  best_strategy: string;
  success_rate: number;
  sample_size: number;
}

export interface MatrixStrategyCell {
  success: number;
  fail: number;
  status: 'V' | 'X' | '—';
}

export interface MatrixRow {
  domain: string;
  is_paused: boolean;
  paused_until: string | null;
  pinned_strategy: string | null;
  pinned_by: string | null;
  strategies: Record<string, MatrixStrategyCell>;
}

export const MATRIX_STRATEGY_LABELS: Record<string, string> = {
  basic_ua_rotate: 'UA輪換',
  cookie_farming: 'Cookie養殖',
  tls_simulation: 'TLS模擬',
  quickscraper_domain_parser: 'QS網域',
  quickscraper_standard: 'QS標準',
  enhanced_headers: '增強標頭',
  mobile_ua: '行動UA',
  mobile_site: '行動站點',
  quickscraper_premium: 'QS住宅',
  quickscraper_json: 'QS JSON',
  flaresolverr: 'FlareSolverr',
  cf_bypass: 'CF繞過',
  browserless_unblock: 'BL Unblock',
  browseract_us: 'BrowserAct',
};

// ── API ──

export const crawlStrategyApi = {
  getAll: () => api.get<CrawlStrategyConfig[]>('/crawl-strategies'),

  getByDomain: (domain: string) =>
    api.get<CrawlStrategyConfig[]>(`/crawl-strategies/${encodeURIComponent(domain)}`),

  upsert: (data: Partial<CrawlStrategyConfig> & { domain: string; strategy: string }) =>
    api.post<CrawlStrategyConfig>('/crawl-strategies', data),

  getLogs: (domain: string, limit = 50, offset = 0) =>
    api.get<CrawlAttemptLog[]>(`/crawl-strategies/${encodeURIComponent(domain)}/logs?limit=${limit}&offset=${offset}`),

  getStats: () => api.get<DomainStats[]>('/crawl-strategies/stats'),

  testUrl: (domain: string, url: string) =>
    api.post<TestResult>(`/crawl-strategies/${encodeURIComponent(domain)}/test`, { url }),

  autoDetect: () => api.post<AutoDetectResult[]>('/crawl-strategies/auto-detect'),

  getMatrix: () => api.get<MatrixRow[]>('/crawl-strategies/matrix'),

  pinStrategy: (domain: string, strategy_name: string) =>
    api.post<{ domain: string; pinned_strategy: string }>('/crawl-strategies/pin', { domain, strategy_name }),

  unpinStrategy: (domain: string) =>
    api.delete<{ domain: string }>(`/crawl-strategies/${encodeURIComponent(domain)}/pin`),

  resetDomain: (domain: string) =>
    api.delete<{ domain: string }>(`/crawl-strategies/${encodeURIComponent(domain)}/reset`),

  resetAll: () =>
    api.delete<{ cleared_cache: number; cleared_pauses: number; cleared_configs: number }>('/crawl-strategies/reset-all'),
};
